// client/components/widgets/WidgetAddModal.jsx
// ✅ Modal zum Hinzufügen von Widgets mit Position-Auswahl
import React, { useMemo, useState } from "react";
import SimpleModal from "./SimpleModal.jsx";
import { useTheme } from "../../context/ThemeContext.jsx";
import { tw, cn } from "../../utils/themeClasses.js";
import { WIDGET_REGISTRY, WIDGET_CATEGORIES } from "./registry.js";
import { Check, Plus, ArrowLeft, ArrowRight } from "lucide-react";
import AppButton from "../ui/AppButton.jsx";

/**
 * WidgetAddModal - Modal zum Auswählen und Hinzufügen von Widgets
 * User kann Widget UND Position (links/rechts) wählen
 */
export default function WidgetAddModal({
  isOpen,
  onClose,
  onAdd,
  defaultPosition = "left",
  existingWidgetIds = [],
  availableWidgets = [],
  leftCount = 0,
  rightCount = 0,
  maxPerRail = 6
}) {
    const [selectedWidget, setSelectedWidget] = useState(null);

  // Widgets nach Kategorie gruppieren (ALLE, nicht nur MVP)
  const groupedWidgets = useMemo(() => {
    const groups = {};
    const allowedIds = availableWidgets.length > 0 ? new Set(availableWidgets.map(w => w.id)) : new Set(Object.keys(WIDGET_REGISTRY));
    for (const widget of Object.values(WIDGET_REGISTRY)) {
      // Alle Widgets anzeigen (auch v1)
      if (availableWidgets.length > 0 && !allowedIds.has(widget.id)) continue;
      const category = widget.category || "other";
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(widget);
    }
    return groups;
  }, [availableWidgets]);
  const categoryOrder = ["info", "personal", "tasks", "actions", "team", "business", "fun"];
  const handleSelectWidget = widgetId => {
    if (existingWidgetIds.includes(widgetId)) return;
    setSelectedWidget(widgetId);
  };
  const handleAddToPosition = position => {
    if (!selectedWidget) return;
    onAdd?.(selectedWidget, position);
    setSelectedWidget(null);
  };
  const handleClose = () => {
    setSelectedWidget(null);
    onClose?.();
  };
  const leftFull = leftCount >= maxPerRail;
  const rightFull = rightCount >= maxPerRail;
  return <SimpleModal open={isOpen} onClose={handleClose} title="Widget hinzufügen" maxWidth={600}>
      <div className="space-y-4">
        {/* Position Selector - zeigt wenn Widget ausgewählt */}
        {selectedWidget && <div className={cn("p-4 rounded-xl border mb-4", "bg-blue-500/10 border-blue-500/30")}>
            <p className={cn("text-sm font-medium mb-3 text-center", tw.textSecondary(true))}>
              Wo soll "{WIDGET_REGISTRY[selectedWidget]?.name}" erscheinen?
            </p>
            <div className="flex gap-3 justify-center">
              <AppButton type="button" variant="ghost" onClick={() => handleAddToPosition("left")} disabled={leftFull} className={cn("flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm", "transition-all", leftFull ? "opacity-50 cursor-not-allowed bg-slate-500/20" : "bg-blue-500/20 text-blue-400 hover:bg-blue-500/30")}>
                <ArrowLeft size={16} />
                Links ({leftCount}/{maxPerRail})
              </AppButton>
              <AppButton type="button" variant="ghost" onClick={() => handleAddToPosition("right")} disabled={rightFull} className={cn("flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm", "transition-all", rightFull ? "opacity-50 cursor-not-allowed bg-slate-500/20" : "bg-green-500/20 text-green-400 hover:bg-green-500/30")}>
                Rechts ({rightCount}/{maxPerRail})
                <ArrowRight size={16} />
              </AppButton>
            </div>
            <AppButton type="button" variant="ghost" onClick={() => setSelectedWidget(null)} className={cn("w-full mt-2 text-xs min-h-[44px] h-auto font-normal", tw.textMuted(true))}>
              Abbrechen
            </AppButton>
          </div>}

        {/* Widget List */}
        {categoryOrder.map(categoryId => {
        const widgets = groupedWidgets[categoryId];
        if (!widgets || widgets.length === 0) return null;
        const category = WIDGET_CATEGORIES[categoryId];
        return <div key={categoryId}>
              <div className="mb-2">
                <h3 className={cn("text-xs font-semibold uppercase tracking-wide", tw.textMuted(true))}>
                  {category?.name || categoryId}
                </h3>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {widgets.map(widget => {
              const isActive = existingWidgetIds.includes(widget.id);
              const isSelected = selectedWidget === widget.id;
              const Icon = widget.icon;
              return <AppButton key={widget.id} type="button" variant="ghost" onClick={() => handleSelectWidget(widget.id)} disabled={isActive} className={cn("flex items-center gap-2 p-2.5 rounded-lg border text-left", "transition-all duration-150", isActive ? cn("cursor-default opacity-60", "bg-white/5 border-white/10") : isSelected ? cn("bg-blue-500/20 border-blue-500/50 ring-2 ring-blue-500/30") : cn("cursor-pointer", "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"))}>
                      {Icon && <Icon size={16} className={cn(isActive ? tw.textMuted(true) : isSelected ? "text-blue-400" : "text-blue-400")} />}
                      <span className={cn("text-sm font-medium truncate", tw.textSecondary(true))}>
                        {widget.name}
                      </span>
                      {isActive && <Check size={14} className={"text-green-400 ml-auto"} />}
                    </AppButton>;
            })}
              </div>
            </div>;
      })}

        {Object.keys(groupedWidgets).length === 0 && <div className="text-center py-8">
            <p className={tw.textMuted(true)}>Keine Widgets verfügbar.</p>
          </div>}
      </div>
    </SimpleModal>;
}